import { createContext, FC, MouseEvent, ReactElement, ReactNode, RefObject, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	AwardRibbonOutline,
	Box,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	CogOutline,
	DotsHorizontalOutline,
	HistoryOutline,
	HomeOutline,
	HStack,
	LeftDrawerCollapseOutline,
	LeftDrawerExpandOutline,
	rem,
	ShoppingCartOutline,
	Stack,
	TargetOutline,
	Text,
	Tooltip,
	VStack,
} from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { Permission, ProtectedContent, useCurrentCompany, useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { Conceal } from '../../../components/display/conceal';
import { SeasonalToggle } from '../../../components/seasonal/seasonal-toggle';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { useLeaderboardPanel } from '../../../hooks/use-leaderboard-panel';
import { AppPath } from '../../admin-navbar/config/router';
import { useCurrentUserReports } from '../../team-management/hooks/use-current-user-reports';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useAdminNavLink } from '../hooks/use-admin-nav-link';
import { CompanyLogo } from './company-logo';
import { CreateNewMenu } from './create-new-menu';
import { IconAndTextContent } from './icon-and-text-content';
import { LeaderboardPanel } from './leaderboard';
import { NotificationPanel } from './notification';
import { SearchNavItem } from './search-nav-item';
import { TeamMenu } from './team-menu';
import { TeamPanel } from './team-panel';
import { TrialCard } from './trial-card';
import { UserMenu } from './user-menu';
import { UserNavAction } from './user-nav-action';
import { UserNavLink } from './user-nav-link';
import { UserNavMenu } from './user-nav-menu';

// Context to allow nav items to close the team panel when clicked
const TeamPanelContext = createContext<{ closeTeamPanel: () => void }>({ closeTeamPanel: () => {} });
export const useCloseTeamPanel = () => useContext(TeamPanelContext);

interface UserNavbarProps {
	isExpanded: boolean;
	toggleSidebar: () => void;
}

export const UserNavbar: FC<UserNavbarProps> = ({ isExpanded, toggleSidebar }) => {
	const { t } = useTranslation('navigation');
	const { t: c } = useTranslation('common');
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const user = useCurrentUser();
	const isAdmin = user.global_admin;
	const { hide_competitive_features: hideCompetitiveFeatures } = useCurrentCompany();
	const hideLeaderboard = hideCompetitiveFeatures && !isAdmin;
	const isLeaderboardMiniEnabled = useFeatureFlag('leaderboard-mini');

	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const adminNavLink = useAdminNavLink();
	const { unreadCount } = useFetchNotifications(user.locale);
	const { data: directReports = [] } = useCurrentUserReports();

	// Panel states
	const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
	const [isTeamPanelOpen, setIsTeamPanelOpen] = useState(false);
	const { isOpen: isLeaderboardPanelOpen, onOpen: openLeaderboardPanel, onClose: closeLeaderboardPanel } = useLeaderboardPanel();

	// Scrollable nav overflow detection
	const scrollableNavRef = useRef<HTMLDivElement>(null);
	const hasOverflow = useHasOverflow(scrollableNavRef);

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	const handleOpenNotifications = () => {
		setIsTeamPanelOpen(false);
		closeLeaderboardPanel();
		setIsNotificationPanelOpen(true);
	};

	const handleCloseNotifications = () => {
		setIsNotificationPanelOpen(false);
	};

	const handleToggleTeamPanel = () => {
		setIsNotificationPanelOpen(false);
		closeLeaderboardPanel();
		setIsTeamPanelOpen((prev) => !prev);
	};

	const handleCloseTeamPanel = () => {
		setIsTeamPanelOpen(false);
	};

	const handleToggleLeaderboardPanel = () => {
		setIsNotificationPanelOpen(false);
		setIsTeamPanelOpen(false);
		if (isLeaderboardPanelOpen) {
			closeLeaderboardPanel();
		} else {
			openLeaderboardPanel();
		}
	};

	return (
		<TeamPanelContext.Provider value={{ closeTeamPanel: handleCloseTeamPanel }}>
			<VStack bgColor="bg.canvas" w="full" h="100%" px={rem(8)} py={rem(20)} spacing={0} align="flex-start">
				<HStack justifyContent="flex-start" height={rem(36)} width="100%" px={rem(10)} mb={rem(24)} spacing={0} position="relative">
					<Box
						as={Link}
						id="primary-nav-logo"
						h={rem(36)}
						to={homeURL}
						onClick={handleLogoClick}
						opacity={isExpanded ? 1 : 0}
						overflow="hidden"
						pointerEvents={isExpanded ? 'auto' : 'none'}
						transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4,0,.2,1)'}
						flexShrink={0}
					>
						<CompanyLogo />
					</Box>

					<Tooltip label={isExpanded ? t('collapseSidebar') : t('expandSidebar')} placement="right" openDelay={900}>
						<Box
							as="button"
							aria-label={isExpanded ? t('collapseSidebar') : t('expandSidebar')}
							display="flex"
							alignItems="center"
							justifyContent="center"
							p={3}
							borderRadius="md"
							cursor="pointer"
							color="text.softer"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={toggleSidebar}
							position="absolute"
							right={0}
						>
							{isExpanded ? (
								<LeftDrawerCollapseOutline data-testid="collapse-sidebar" size="sm" />
							) : (
								<LeftDrawerExpandOutline data-testid="expand-sidebar" size="sm" />
							)}
						</Box>
					</Tooltip>
				</HStack>

				<Stack as="nav" spacing={2} flexDir="column" width="100%" flex={1} minHeight={0}>
					{/* Scrollable nav items */}
					<Box
						ref={scrollableNavRef}
						flex={1}
						overflowY="auto"
						overflowX="hidden"
						minHeight={0}
						borderBottom={hasOverflow ? '1px solid' : 'none'}
						borderColor="border.default"
						mx={rem(-8)}
						px={rem(8)}
						pb={hasOverflow ? rem(8) : 0}
						sx={{
							'&::-webkit-scrollbar': { width: '6px' },
							'&::-webkit-scrollbar-track': { background: 'transparent' },
							'&::-webkit-scrollbar-thumb': {
								background: 'scrollbar.thumb',
								borderRadius: '3px',
							},
							'&::-webkit-scrollbar-thumb:hover': { background: 'scrollbar.thumbHover' },
							scrollbarColor: 'var(--chakra-colors-scrollbar-thumb) transparent',
							scrollbarWidth: 'thin',
						}}
					>
						<VStack spacing={1} align="stretch">
							{/* HOME */}
							<UserNavLink to="/" data-testid="nav-link-home" label={t('links.home')} isExpanded={isExpanded}>
								<IconAndTextContent icon={<HomeOutline size="md" />} label={t('links.home')} isExpanded={isExpanded} />
							</UserNavLink>

							{/* SEARCH */}
							<SearchNavItem isExpanded={isExpanded} />

							{/* 1:1s */}
							<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
								<UserNavLink to="/meet" data-testid="nav-link-one-on-ones" label={t('links.oneOnOnes')} isExpanded={isExpanded}>
									<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* GOALS */}
							<ProtectedContent permissions={Permission.CanAccessGoals}>
								<UserNavLink to="/growth/goals" data-testid="nav-link-goals" label={t('links.goals')} isExpanded={isExpanded}>
									<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* RECAPS */}
							<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
								<UserNavLink to="/growth/recap-review" data-testid="nav-link-recaps" label={t('links.recaps')} isExpanded={isExpanded}>
									<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* REWARDS */}
							<UserNavLink to="/rewards" data-testid="nav-link-rewards" label={t('links.rewards')} isExpanded={isExpanded}>
								<IconAndChildContent icon={<ShoppingCartOutline size="md" />} isExpanded={isExpanded}>
									<HStack justifyContent="space-between" alignItems="flex-start" width="100%">
										<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
											{t('links.rewards')}
										</Text>
										<Box bgColor="points.bgAlpha" fontWeight="semibold" fontSize={1} color="points.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
											+{c('intl_number', { value: user.earning_balance })}
										</Box>
									</HStack>
								</IconAndChildContent>
							</UserNavLink>

							{/* MORE MENU ITEMS */}
							<UserNavMenu buttonIcon={<DotsHorizontalOutline size="md" />} buttonText={t('moreButtonText')} isExpanded={isExpanded}>
								{/* INCENTIVES */}
								<ProtectedContent permissions={Permission.CanAccessIncentives}>
									<UserNavLink to="/incentives" data-testid="nav-link-incentives">
										<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded />
									</UserNavLink>
								</ProtectedContent>
								{/* LEADERBOARD */}
								<ProtectedContent permissions={Permission.CanAccessAnalytics}>
									<Conceal if={hideLeaderboard}>
										{isLeaderboardMiniEnabled ? (
											<UserNavAction
												onClick={handleToggleLeaderboardPanel}
												isActive={isLeaderboardPanelOpen}
												data-testid="nav-link-leaderboard"
												label={t('links.leaderboard')}
												isExpanded
											>
												<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
											</UserNavAction>
										) : (
											<UserNavLink to="/analytics/leaderboards" isRecognizeRoute={true} data-testid="nav-link-leaderboard">
												<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
											</UserNavLink>
										)}
									</Conceal>
								</ProtectedContent>
								{/* FEEDBACK */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<UserNavLink to="/growth/feedback" data-testid="nav-link-feedback">
										<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded />
									</UserNavLink>
								</ProtectedContent>
							</UserNavMenu>

							{/* TEAM NAV ITEMS */}
							<ProtectedContent permissions={Permission.CanAccessTeamHub}>
								<TeamMenu isExpanded={isExpanded} isTeamPanelOpen={isTeamPanelOpen} onToggleTeamPanel={handleToggleTeamPanel} />
							</ProtectedContent>

							{/* CREATE NEW MENU */}
							<CreateNewMenu isExpanded={isExpanded} />
						</VStack>
					</Box>

					{/* Fixed bottom items - always visible */}
					<VStack spacing={1} flexShrink={0}>
						{/* SEASONAL THEME TOGGLE */}
						<SeasonalToggle isExpanded={isExpanded} />

						{/* TRIAL CARD */}
						<TrialCard isExpanded={isExpanded} />

						{/* ADMIN MENU */}
						<ProtectedContent permissions={Permission.CanViewAdmin}>
							<UserNavLink
								to={adminNavLink.to}
								isRecognizeRoute={adminNavLink.isRecognizeRoute}
								data-testid="nav-link-admin"
								label={t('links.adminSettings')}
								isExpanded={isExpanded}
							>
								<IconAndTextContent icon={<CogOutline size="md" />} label={t('links.adminSettings')} isExpanded={isExpanded} />
							</UserNavLink>
						</ProtectedContent>

						{/* USER MENU */}
						<UserMenu isExpanded={isExpanded} onOpenNotifications={handleOpenNotifications} unreadCount={unreadCount} />
					</VStack>
				</Stack>

				{/* Notification Panel */}
				<NotificationPanel isOpen={isNotificationPanelOpen} onClose={handleCloseNotifications} isSidebarExpanded={isExpanded} />

				{/* Team Panel */}
				<TeamPanel isOpen={isTeamPanelOpen} onClose={handleCloseTeamPanel} isSidebarExpanded={isExpanded} directReports={directReports} />

				{/* Leaderboard Panel */}
				{isLeaderboardMiniEnabled && <LeaderboardPanel isOpen={isLeaderboardPanelOpen} onClose={closeLeaderboardPanel} isSidebarExpanded={isExpanded} />}
			</VStack>
		</TeamPanelContext.Provider>
	);
};

type IconAndChildContentProps = {
	icon: ReactElement;
	isExpanded: boolean;
	children: ReactNode;
};

const IconAndChildContent: FC<IconAndChildContentProps> = ({ children, icon, isExpanded }) => (
	<HStack justifyContent="flex-start" alignItems="flex-start" spacing={3} w="100%">
		{icon}
		<Box opacity={isExpanded ? 1 : 0} transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'} flex={1}>
			{children}
		</Box>
	</HStack>
);

function useHasOverflow(ref: RefObject<HTMLDivElement>) {
	const [hasOverflow, setHasOverflow] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const checkOverflow = () => {
			setHasOverflow(element.scrollHeight > element.clientHeight);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [ref]);

	return hasOverflow;
}
